import { useState, useRef, useEffect } from 'react'

/**
 * MultiSelectDropdown Component
 * A searchable dropdown that allows selecting multiple options
 * with tags displayed for selected items.
 */
function MultiSelectDropdown({
  options,
  selectedValues = [],
  onChange,
  placeholder = 'Select options...',
  searchPlaceholder = 'Search...',
  maxDisplayTags = 3,
  disabled = false,
  className = ''
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef(null)
  const searchInputRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Handle option toggle
  const handleToggle = (optionValue) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter(v => v !== optionValue)
      : [...selectedValues, optionValue]
    onChange(newValues)
  }

  // Handle select all visible
  const handleSelectAll = () => {
    const visibleValues = filteredOptions.map(o => o.value)
    const newValues = [...new Set([...selectedValues, ...visibleValues])]
    onChange(newValues)
  }

  // Handle clear all
  const handleClearAll = () => {
    onChange([])
  }

  // Get selected option labels
  const getSelectedLabels = () => {
    return selectedValues
      .map(value => options.find(o => o.value === value)?.label)
      .filter(Boolean)
  }

  const selectedLabels = getSelectedLabels()

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Trigger */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setIsOpen(!isOpen) } }}
        className={`w-full min-h-[42px] px-3 py-2 text-left border rounded-lg transition-colors flex items-center justify-between gap-2 ${
          disabled
            ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
            : 'bg-white dark:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer'
        } ${
          isOpen
            ? 'border-primary-500 ring-2 ring-primary-500'
            : 'border-gray-300 dark:border-gray-600'
        }`}
      >
        <div className="flex-1 flex flex-wrap gap-1 min-h-[24px]">
          {selectedLabels.length === 0 ? (
            <span className="text-gray-400 dark:text-gray-500 text-sm">{placeholder}</span>
          ) : selectedLabels.length <= maxDisplayTags ? (
            selectedLabels.map((label, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-accent-200"
              >
                {label}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    const value = options.find(o => o.label === label)?.value
                    if (value) handleToggle(value)
                  }}
                  className="ml-1 hover:text-primary-900 dark:hover:text-primary-100"
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </span>
            ))
          ) : (
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {selectedLabels.length} selected
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Quick Actions */}
          <div className="px-2 py-1.5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-primary-600 dark:text-accent-300 hover:text-primary-700 dark:hover:text-accent-200"
            >
              Select all {searchTerm ? 'visible' : ''}
            </button>
            <span className="text-gray-400">|</span>
            <button
              type="button"
              onClick={handleClearAll}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Clear all
            </button>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value)
                return (
                  <label
                    key={option.value}
                    className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                      isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggle(option.value)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <span className={`ml-2 text-sm ${
                      isSelected
                        ? 'text-gray-900 dark:text-white font-medium'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {option.label}
                    </span>
                    {option.hasPrivacyLaw && (
                      <span className="ml-auto text-xs text-purple-600 dark:text-purple-400">
                        Privacy Law
                      </span>
                    )}
                  </label>
                )
              })
            )}
          </div>

          {/* Selected Count Footer */}
          {selectedValues.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
              {selectedValues.length} of {options.length} selected
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default MultiSelectDropdown
